import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from 'sweetalert2';
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { useSidebar } from "../../SidebarContext";
import Header from "../Header";
import Sidebar from "../SideBar";
import { User, StickyNote, ArrowLeftIcon, PenIcon, Trash2Icon, FileText, Download, ArrowRightLeft, ReceiptIndianRupee, MinusCircle, Send } from "lucide-react";
import { toast } from "react-toastify";
import { decryptToken } from "../utils/getToken";
import OmsSpinnerData from "../Loader/Spinner";
import { Document, Page, Text, View, StyleSheet, Image, Link, pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { parseDocument } from 'htmlparser2';
import { DomUtils } from 'htmlparser2';
import DOMPurify from "dompurify";
import IssueCreditNoteModal from "./IssueCreditNoteModal";
import {
  describeDeliveryOutcome,
  toastDeliveryOutcome,
} from "../../utils/describeDeliveryOutcome";

function quoteConversionSwalHtml(quoteNumber) {
    return `
      <p style="margin-bottom:12px">Quote <strong>${quoteNumber}</strong> will become a published invoice with a new invoice number.</p>
      <label style="display:block;margin:8px 0;text-align:left">
        <input id="swal-send-mail" type="checkbox" /> Email invoice to customer
      </label>
      <label style="display:block;text-align:left">
        <input id="swal-send-whatsapp" type="checkbox" /> Send WhatsApp to customer
      </label>
    `;
}

const InvoiceDetail = () => {

    const { id } = useParams();
    const [invoice, setInvoice] = useState(null);
    const navigate = useNavigate();
    const url = API_BASE_URL;
    const user = getDecryptedUserFromCookie("gta_mainstateuser");
    const token = decryptToken("accessToken") || ""
    const [downloadingId, setDownloadingId] = useState(null);
    const [downloadedId, setDownloadedId] = useState(null);
    const [converting, setConverting] = useState(false);
    const [resending, setResending] = useState(false);
    const [creditNotes, setCreditNotes] = useState([]);
    const [creditModalOpen, setCreditModalOpen] = useState(false);
    const [authorityData, setAuthorityData] = useState({});
    const [bankDetails, setBankDetails] = useState({})
    const [customer, setCustomer] = useState({})

    useEffect(() => {
        const fetchInvoice = async () => {
            try {
                const res = await fetch(`${url}/newinvoice/singleinvoice/${id}`, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                });
                const result = await res.json();
                setInvoice(result);
            } catch (error) {
                console.error("Error loading invoice:", error);
                setInvoice(null);
            }
        };

        if (id) fetchInvoice();
    }, [id]);

    const fetchCreditNotes = async () => {
        if (!id) return;
        try {
            const res = await fetch(`${url}/newinvoice/credit-notes/for-invoice/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                setCreditNotes(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            console.error("fetchCreditNotes:", err);
        }
    };

    useEffect(() => {
        if (!invoice) return;
        const docType = String(invoice.document_type || "").toLowerCase();
        if (docType === "quote" || docType === "proforma" || docType === "credit_note") return;
        fetchCreditNotes();
    }, [invoice?.id, invoice?.pending]);



    const handleEdit = () => {
        const docType = String(invoice?.document_type || "").toLowerCase();
        const isQuote = docType === "quote" || docType === "proforma";
        if (isQuote) {
            navigate('/new-quote', { state: { invoice } });
        } else {
            navigate(`/edit-invoice/${invoice.id}`, { state: { invoice } });
        }
    };

    const isQuoteDocument = () => {
        const docType = String(invoice?.document_type || "").toLowerCase();
        return docType === "quote" || docType === "proforma";
    };

    const isCreditNoteDocument = (row) => {
        const docType = String((row || invoice)?.document_type || "").toLowerCase();
        return docType === "credit_note";
    };

    const handleConvertToInvoice = async () => {
        const result = await Swal.fire({
            title: "Convert to invoice?",
            html: quoteConversionSwalHtml(invoice.invoice_number),
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Convert",
            preConfirm: () => ({
                send_mail: document.getElementById("swal-send-mail")?.checked || false,
                send_whatsapp: document.getElementById("swal-send-whatsapp")?.checked || false,
            }),
        });
        if (!result.isConfirmed) return;

        setConverting(true);
        try {
            const res = await fetch(`${url}/newinvoice/convert-to-invoice/${invoice.id}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    publish: true,
                    send_mail: result.value?.send_mail === true,
                    send_whatsapp: result.value?.send_whatsapp === true,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Conversion failed");
            const deliveryParts = describeDeliveryOutcome(data);
            const base = `Created invoice ${data.invoice?.invoice_number}`;
            if (deliveryParts.length) {
                const hasError = deliveryParts.some((p) => p.includes("failed"));
                const msg = `${base}. ${deliveryParts.join("; ")}.`;
                if (hasError) toast.error(msg);
                else toast.success(msg);
            } else {
                toast.success(base);
            }
            if (data.invoice?.id) {
                navigate(`/invoice-detail/${data.invoice.id}`);
            }
        } catch (err) {
            toast.error(err.message || "Could not convert quote");
        } finally {
            setConverting(false);
        }
    };

    const handleResendInvoice = async () => {
        if (!invoice) return;
        const result = await Swal.fire({
            title: "Resend invoice?",
            html: `
              <p style="margin-bottom:12px">Send invoice <strong>${invoice.invoice_number}</strong> to the customer again.</p>
              <label style="display:block;margin:8px 0;text-align:left">
                <input id="swal-resend-mail" type="checkbox" checked /> Email invoice to customer
              </label>
              <label style="display:block;text-align:left">
                <input id="swal-resend-whatsapp" type="checkbox" /> Send WhatsApp to customer
              </label>
            `,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Send",
            preConfirm: () => ({
                send_mail: document.getElementById("swal-resend-mail")?.checked !== false,
                send_whatsapp: document.getElementById("swal-resend-whatsapp")?.checked === true,
            }),
        });
        if (!result.isConfirmed) return;

        setResending(true);
        try {
            const res = await fetch(`${url}/newinvoice/send/${invoice.id}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    send_mail: result.value?.send_mail !== false,
                    send_whatsapp: result.value?.send_whatsapp === true,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Could not resend invoice");
            const { message, type } = toastDeliveryOutcome("Invoice resend attempted.", data);
            if (type === "error") toast.error(message);
            else if (type === "warning") toast.warning(message);
            else toast.success(message);
        } catch (err) {
            toast.error(err.message || "Could not resend invoice");
        } finally {
            setResending(false);
        }
    };

    const handleRecordPayment = () => {
        if (!invoice) return;
        const docType = String(invoice.document_type || "").toLowerCase();
        if (docType === "quote" || docType === "proforma" || docType === "credit_note") return;
        const today = new Date().toLocaleDateString("en-CA");
        navigate("/receipt", {
            state: {
                openReceipt: true,
                prefillReceipt: {
                    customerId: invoice.customer_id,
                    customerName: invoice.customer_name || "",
                    invoiceNumber: invoice.invoice_number || "",
                    amount: amountRemaining > 0 ? amountRemaining.toFixed(2) : "",
                    receiptDate: today,
                },
            },
        });
    };

    const handleDelete = async (invoiceId) => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'This invoice will be permanently deleted!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await fetch(`${url}/newinvoice/${invoiceId}`, {
                        method: 'DELETE',
                        headers: {
                            Authorization: `Bearer ${token}`,
                        }
                    });

                    if (!response.ok) {
                        const err = await response.json();
                        throw new Error(err?.message || 'Failed to delete');
                    }

                    Swal.fire('Deleted!', 'Document has been deleted.', 'success');
                    const docType = String(invoice?.document_type || "").toLowerCase();
                    if (docType === "quote" || docType === "proforma") {
                        navigate("/quotes");
                    } else if (docType === "credit_note") {
                        navigate("/credit-notes");
                    } else {
                        navigate("/invoice");
                    }
                } catch (error) {
                    console.error('Delete error:', error.message);
                    Swal.fire('Error!', `Failed to delete invoice. ${error.message}`, 'error');
                }
            }
        });
    };

    const fetchBankDetails = async () => {
        try {
            const response = await fetch(`${url}/bank-detail/get`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            })
            const res = await response.json();
            setBankDetails(res.data);

        } catch (error) {
            console.error(error)
        }
    }

    const fetchAuthoritySign = async () => {
        try {
            const response = await fetch(`${url}/authority-sign/get`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            })
            const res = await response.json();
            setAuthorityData({
                sign: res.data.sign
                    ? res.data.sign.replace(/^\/?uploads\/signatures\//, "")
                    : "",
                company_name: res.data.company_name,
            });

        } catch (error) {
            console.error(error)
        }
    }

    /** 1×1 PNG — @react-pdf must not use remote http(s) Image src (CORS → "Failed to fetch"). */
    const PLACEHOLDER_LOGO_DATA_URI =
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==";

    const toBase64 = async (resourceUrl, fetchOpts = {}) => {
        const response = await fetch(resourceUrl, {
            mode: "cors",
            credentials: "omit",
            ...fetchOpts,
        });
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };


    const resolveInvoiceCustomerId = (inv) =>
        inv?.customer_id ?? inv?.cust_ID ?? inv?.customerId ?? null;

    const downloadInvoicePDF = async (invoice) => {
        const orderId = invoice.order_number;
        setDownloadingId(orderId);
        setDownloadedId(null);
        const customerForPdf = await fetchCustomerSingle(resolveInvoiceCustomerId(invoice));
        if (!customerForPdf) {
            alert("Customer is missing for this invoice; cannot build PDF.");
            setDownloadingId(null);
            return;
        }
        const mainId = user?.main_id;
        if (!mainId) {
            alert("Session is missing business id; log in again and retry.");
            setDownloadingId(null);
            return;
        }
        try {
            const profileRes = await fetch(`${url}/register/single/${mainId}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            const res = await profileRes.json();
            if (!profileRes.ok || !res.users) {
                alert(
                    res?.message ||
                        `Could not load business profile (${profileRes.status}).`
                );
                return;
            }
            const profileData = res.users;

            let base64Logo = null;
            let base64Sign = null;
            if (authorityData?.sign) {
                try {
                    base64Sign = await toBase64(
                        `${url}/signatures/${authorityData.sign}`
                    );
                } catch (e) {
                    console.warn("Signature load failed:", e);
                }
            }
            if (profileData?.profilePic) {
                const imageUrl = `${url}/logo/${profileData.profilePic}`;
                try {
                    base64Logo = await toBase64(imageUrl);
                } catch (error) {
                    console.warn("Logo conversion failed:", error);
                    base64Logo = PLACEHOLDER_LOGO_DATA_URI;
                }
            }
            const blob = await pdf(
                <InvoiceDocument invoice={invoice} profile={profileData} logoBase64={base64Logo} bankDetails={bankDetails} authorityData={authorityData} base64Sign={base64Sign} customer={customerForPdf} />
            ).toBlob();
            saveAs(blob, `Invoice_${invoice.invoice_number}.pdf`);

            setDownloadedId(orderId);
            setTimeout(() => setDownloadedId(null), 1200);
        } catch (err) {
            alert(
                err?.message
                    ? `Failed to download invoice: ${err.message}`
                    : "Failed to download invoice"
            );
            console.error("Download error:", err);
        } finally {
            setDownloadingId(null);
        }
    };

    const fetchCustomerSingle = async (customerId) => {
        const id =
            customerId !== undefined &&
            customerId !== null &&
            String(customerId).trim() !== "" &&
            String(customerId) !== "undefined"
                ? String(customerId).trim()
                : null;
        if (!id) return null;
        try {
            const response = await fetch(
                `${url}/api/customer/single/${encodeURIComponent(id)}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const res = await response.json();
            if (!response.ok) {
                console.warn("fetchCustomerSingle:", res?.error || response.status);
                return null;
            }
            const c = res.customer;
            setCustomer(c || {});
            return c || null;
        } catch (error) {
            console.error(error);
            return null;
        }
    };



    let parsedItems = [];
    try {
        const raw = invoice?.items;
        if (raw == null) {
            parsedItems = [];
        } else if (Array.isArray(raw)) {
            parsedItems = raw;
        } else if (typeof raw === "string") {
            const p = JSON.parse(raw);
            parsedItems = Array.isArray(p) ? p : [];
        }
    } catch (e) {
        console.error("Failed to parse items:", e);
        parsedItems = [];
    }
    if (!Array.isArray(parsedItems)) {
        parsedItems = [];
    }

    const getStatusStyle = (status) => {
        switch ((status || '').toLowerCase()) {
            case 'pending':
                return { backgroundColor: '#f0f0f0', color: '#333' };
            case 'paid':
                return { backgroundColor: '#d4edda', color: '#155724' };
            case 'overdue':
                return { backgroundColor: '#f8d7da', color: '#721c24' };
            default:
                return {};
        }
    };


    useEffect(() => {
        fetchAuthoritySign();
        fetchBankDetails();
        fetchCustomerSingle();
    }, [])

    if (!invoice) return <OmsSpinnerData />;

    const quoteDoc = isQuoteDocument();
    const creditDoc = isCreditNoteDocument();
    const quoteConverted = Boolean(invoice.converted_to_id);
    const listPath = quoteDoc ? "/quotes" : creditDoc ? "/credit-notes" : "/invoice";
    const grandTotal = parseFloat(invoice.grand_total) || 0;
    const amountPaid = parseFloat(invoice.advance_payment) || 0;
    const amountRemaining = parseFloat(invoice.pending) || 0;
    const canRecordPayment = !quoteDoc && !creditDoc && amountRemaining > 0.009;
    const canIssueCredit = !quoteDoc && !creditDoc && amountRemaining > 0.009;
    const isPublished = String(invoice.dpstatus || "").toLowerCase() === "published";
    const canResend = !quoteDoc && !creditDoc && isPublished;

    return (
        <>
            <IssueCreditNoteModal
                isOpen={creditModalOpen}
                onClose={() => setCreditModalOpen(false)}
                invoice={invoice}
                onSuccess={async () => {
                    const res = await fetch(`${url}/newinvoice/singleinvoice/${id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    const updated = await res.json();
                    setInvoice(updated);
                    fetchCreditNotes();
                }}
            />
            <div className="ecom-customer-detail-container">
                <div className="ecom-customer-header-detail">
                    <div className="ecom-customer-header-left-container">
                        <ArrowLeftIcon onClick={() => navigate(listPath)} />
                        <h2 className="ecom-customer-name-highlight">
                            #{invoice.invoice_number} {invoice.status && (
                                <span
                                    style={{
                                        ...getStatusStyle(invoice.status),
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        fontWeight: 600,
                                        fontSize: '0.85rem',
                                        marginLeft: 8,
                                    }}
                                >
                                    {invoice.status}
                                </span>
                            )}
                        </h2>
                    </div>
                    <div className="ecom-customer-header-right-container">
                        {canResend ? (
                            <button
                                type="button"
                                className="omsnewbutton-copy"
                                onClick={handleResendInvoice}
                                disabled={resending}
                                style={{ marginRight: 12 }}
                            >
                                <Send size={14} style={{ marginRight: 4, verticalAlign: "middle" }} />
                                {resending ? "Sending…" : "Resend"}
                            </button>
                        ) : null}
                        {canIssueCredit ? (
                            <button
                                type="button"
                                className="omsnewbutton-copy"
                                onClick={() => setCreditModalOpen(true)}
                                style={{ marginRight: 12 }}
                            >
                                <MinusCircle size={14} style={{ marginRight: 4, verticalAlign: "middle" }} />
                                Issue credit note
                            </button>
                        ) : null}
                        {canRecordPayment ? (
                            <button
                                type="button"
                                className="omsnewbutton-copy"
                                onClick={handleRecordPayment}
                                style={{ marginRight: 12 }}
                            >
                                <ReceiptIndianRupee size={14} style={{ marginRight: 4, verticalAlign: "middle" }} />
                                Record payment
                            </button>
                        ) : null}
                        {quoteDoc && !quoteConverted ? (
                            <button
                                type="button"
                                className="omsnewbutton-copy"
                                disabled={converting}
                                onClick={handleConvertToInvoice}
                                style={{ marginRight: 12 }}
                            >
                                <ArrowRightLeft size={14} style={{ marginRight: 4, verticalAlign: "middle" }} />
                                {converting ? "Converting…" : "Convert to invoice"}
                            </button>
                        ) : null}
                        {!quoteConverted && !creditDoc ? <PenIcon onClick={handleEdit} /> : null}
                        <Download cursor={"pointer"} onClick={() => downloadInvoicePDF(invoice)} />
                    </div>
                </div>

                {/* 1. Invoice Details */}
                <section className="ecom-customer-section-box ecom-customer-details">
                    <div className="flexit-invoicecenter">
                        <h4><User size={16} style={{ marginRight: '8px' }} /> {quoteDoc ? "Quote details" : creditDoc ? "Credit note details" : "Invoice Details"}</h4>
                        <Trash2Icon onClick={() => handleDelete(invoice.id)} />
                    </div>
                    <div className="ecom-customer-detail-grid">
                        <div><label>Order No:</label><span>{invoice.order_number}</span></div>
                        <div><label>Invoice Date:</label><span>{invoice.invoice_date}</span></div>
                        <div><label>Due Date:</label><span>{invoice.due_date}</span></div>
                        <div><label>Terms:</label><span>{invoice.terms || "—"}</span></div>
                        {!quoteDoc && !creditDoc ? (
                            <>
                                <div><label>Grand total:</label><span>₹ {grandTotal.toFixed(2)}</span></div>
                                <div><label>Amount paid:</label><span>₹ {amountPaid.toFixed(2)}</span></div>
                                <div><label>Amount remaining:</label><span style={{ fontWeight: 600, color: amountRemaining > 0 ? "#b45309" : "#15803d" }}>₹ {amountRemaining.toFixed(2)}</span></div>
                            </>
                        ) : creditDoc ? (
                            <>
                                <div><label>Credit amount:</label><span>₹ {grandTotal.toFixed(2)}</span></div>
                                <div><label>Applied to invoice:</label>
                                    <span>
                                        {invoice.linked_invoice_id ? (
                                            <button
                                                type="button"
                                                className="omsnewbutton-copy"
                                                style={{ padding: "2px 8px", fontSize: "0.85rem" }}
                                                onClick={() => navigate(`/invoice-detail/${invoice.linked_invoice_id}`)}
                                            >
                                                View invoice
                                            </button>
                                        ) : "—"}
                                    </span>
                                </div>
                            </>
                        ) : (
                            <div><label>Quote total:</label><span>₹ {grandTotal.toFixed(2)}</span></div>
                        )}
                    </div>
                </section>

                {/* 2. Items Detail */}
                <section className="ecom-customer-section-box ecom-customer-details">
                    <h4><FileText size={16} style={{ marginRight: '8px' }} /> Items Detail</h4>
                    {parsedItems.length > 0 ? (
                        <div style={{ paddingLeft: '12px' }}>
                            {parsedItems
                                .filter(i => i.item || i.quantity || i.rate || i.amount !== "0.00")
                                .map((item, index) => {
                                    const qty = parseFloat(item.quantity) || 0;
                                    const rate = parseFloat(item.rate) || 0;
                                    const total = (qty * rate);

                                    return (
                                        <div key={index} style={{ borderBottom: '1px solid #eee', paddingBottom: 8, marginBottom: 10 }}>
                                            <div><strong>Item:</strong> {item.item?.title || "—"}</div>
                                            <div><strong>Quantity × Rate:</strong> {item.quantity || "0"} × ₹{item.rate || "0.00"} = ₹{total}</div>
                                        </div>
                                    );
                                })}

                            <div><strong>Subtotal:</strong> ₹ {invoice.subtotal}</div>
                            <div><strong>Discount:</strong> ₹ {invoice.discount_amount}</div>
                            <div><strong>Advance Payment:</strong> ₹ {invoice.advance_payment}</div>
                            <div><strong>Tax:</strong> ₹ {invoice.tax}</div>
                            <div><strong>Shipping / Service Fees:</strong> ₹ {invoice.shipping}</div>
                            <div><strong>Grand Total:</strong> ₹ {invoice.grand_total}</div>
                        </div>
                    ) : (
                        <p style={{ textAlign: "center", margin: "20px 10px" }}>No items found for this invoice.</p>
                    )}
                </section>

                {!quoteDoc && !creditDoc && creditNotes.length > 0 ? (
                    <section className="ecom-customer-section-box ecom-customer-details">
                        <h4><MinusCircle size={16} style={{ marginRight: '8px' }} /> Credit notes</h4>
                        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                            {creditNotes.map((cn) => (
                                <li
                                    key={cn.id}
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        padding: "8px 0",
                                        borderBottom: "1px solid #eee",
                                    }}
                                >
                                    <button
                                        type="button"
                                        className="omsnewbutton-copy"
                                        onClick={() => navigate(`/invoice-detail/${cn.id}`)}
                                    >
                                        {cn.invoice_number}
                                    </button>
                                    <span>₹ {parseFloat(cn.grand_total || 0).toFixed(2)} · {cn.invoice_date}</span>
                                </li>
                            ))}
                        </ul>
                    </section>
                ) : null}

                {/* 3. Customer Note */}
                <section className="ecom-customer-section-box ecom-customer-details">
                    <h4><StickyNote size={16} style={{ marginRight: '8px' }} /> Customer Note</h4>
                    <div
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(invoice.customer_note || "<p>—</p>", {
                                USE_PROFILES: { html: true },
                            }),
                        }}
                    />
                </section>
            </div>
        </>
    );
};

export default InvoiceDetail;





export const renderNoteAsPDF = (html) => {
    const doc = parseDocument(html);
    const nodes = DomUtils.getChildren(doc);

    const renderNodes = (elements, depth = 0) => {
        return elements.map((node, idx) => {
            if (node.type === 'text') {
                return node.data;
            }

            if (node.name === 'br') {
                return <Text key={`br-${depth}-${idx}`}>{'\n'}</Text>;
            }

            if (node.name === 'p') {
                return (
                    <Text key={`p-${depth}-${idx}`} style={styles.paragraph}>
                        {renderNodes(DomUtils.getChildren(node), depth + 1)}
                    </Text>
                );
            }
            if (node.name === 'a') {
                const href = node.attribs?.href || "#";
                const linkText = DomUtils.getText(node); // 🔥 Plain text from inside <a>
                return (
                    <Link
                        key={`a-${depth}-${idx}`}
                        src={href.startsWith("http") ? href : `https://${href}`} // ensure it's clickable
                        style={{ color: 'blue', textDecoration: 'none' }} // removes underline
                    >
                        {linkText}
                    </Link>
                );
            }


            if (node.name === 'u') {
                return (
                    <Text key={`u-${depth}-${idx}`} style={{ textDecoration: 'underline' }}>
                        {renderNodes(DomUtils.getChildren(node), depth + 1)}
                    </Text>
                );
            }



            if (node.name === 'strong' || node.name === 'b') {
                return (
                    <Text key={`b-${depth}-${idx}`} style={{ fontWeight: 'bold' }}>
                        {renderNodes(DomUtils.getChildren(node), depth + 1)}
                    </Text>
                );
            }

            if (node.name === 'em' || node.name === 'i') {
                return (
                    <Text key={`i-${depth}-${idx}`} style={{ fontStyle: 'italic' }}>
                        {renderNodes(DomUtils.getChildren(node), depth + 1)}
                    </Text>
                );
            }

            if (node.name === 'ul') {
                return DomUtils.getChildren(node)
                    .filter(n => n.name === 'li')
                    .map((li, i) => (
                        <Text key={`ul-${depth}-${idx}-${i}`} style={styles.listItem}>
                            • {renderNodes(DomUtils.getChildren(li), depth + 1)}
                        </Text>
                    ));
            }

            if (node.name === 'ol') {
                return DomUtils.getChildren(node)
                    .filter(n => n.name === 'li')
                    .map((li, i) => (
                        <Text key={`ol-${depth}-${idx}-${i}`} style={styles.listItem}>
                            {`${i + 1}. ${renderNodes(DomUtils.getChildren(li), depth + 1)}`}
                        </Text>
                    ));
            }

            // fallback for unknown tags
            return renderNodes(DomUtils.getChildren(node), depth + 1);
        });
    };

    return renderNodes(nodes);
};




const calculateGST = (taxable, taxRate) => {
    const rate = Number(taxRate) || 0;

    if (rate === 0) {
        return {
            cgstRate: 0,
            sgstRate: 0,
            cgst: 0,
            sgst: 0,
        };
    }

    const halfRate = rate / 2;

    return {
        cgstRate: halfRate,
        sgstRate: halfRate,
        cgst: (taxable * halfRate) / 100,
        sgst: (taxable * halfRate) / 100,
    };
};

const numberToIndianWords = (num) => {
    if (!num || isNaN(num)) return "Zero Rupees";

    const a = [
        "", "One", "Two", "Three", "Four", "Five", "Six",
        "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve",
        "Thirteen", "Fourteen", "Fifteen", "Sixteen",
        "Seventeen", "Eighteen", "Nineteen",
    ];

    const b = [
        "", "", "Twenty", "Thirty", "Forty",
        "Fifty", "Sixty", "Seventy", "Eighty", "Ninety",
    ];

    const inWords = (n) => {
        if (n < 20) return a[n];
        if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
        if (n < 1000)
            return a[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + inWords(n % 100) : "");
        return "";
    };

    let number = Math.floor(num);
    let words = "";

    if (number >= 10000000) {
        words += inWords(Math.floor(number / 10000000)) + " Crore ";
        number %= 10000000;
    }
    if (number >= 100000) {
        words += inWords(Math.floor(number / 100000)) + " Lakh ";
        number %= 100000;
    }
    if (number >= 1000) {
        words += inWords(Math.floor(number / 1000)) + " Thousand ";
        number %= 1000;
    }
    if (number > 0) {
        words += inWords(number);
    }

    return words.trim() + " Rupees";
};


/* ---------------- STYLES ---------------- */
const styles = StyleSheet.create({
    page: {
        padding: 16,
        fontSize: 9,
        fontFamily: "Helvetica",
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
    },

    box: {
        borderWidth: 1,
        borderColor: "#000",
    },

    cell: {
        padding: 6,
    },

    bold: {
        fontWeight: "bold",
    },
    gstTable: {
        marginTop: 8,
        borderWidth: 1,
        borderColor: "#000",
    },

    gstRow: {
        flexDirection: "row",
    },

    gstCell: {
        padding: 6,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: "#000",
        textAlign: "center",
    },
    semibold: {
        fontWeight: 500,
        marginBottom: 5,
    },
    gstHeader: {
        backgroundColor: "#eaeaea",
        fontWeight: "bold",
        textAlign: "center",
    },

    gstRight: {
        textAlign: "right",
    },

    gstBold: {
        fontWeight: "bold",
    },
    itemTable: {
        borderWidth: 1,
        borderColor: "#000",
    },

    itemRow: {
        flexDirection: "row",
    },

    itemHeaderCell: {
        padding: 6,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: "#000",
        backgroundColor: "#f2f2f2",
        fontWeight: "bold",
        textAlign: "center",
    },

    itemCell: {
        padding: 6,
        borderRightWidth: 1,
        // borderBottomWidth: 1,
        borderColor: "#000",
    },
    itemlastcelltable: {
        padding: 6,
        borderRightWidth: 1,
        // borderBottomWidth: 1,
        borderColor: "#000",
    },

    itemLastCell: {
        padding: 6,
        // borderBottomWidth: 1,
        borderColor: "#000",
    },
    itemLastCelltable: {
        padding: 6,
        // borderBottomWidth: 1,
        borderColor: "#000",
    },

    marginTopitemCell: {
        marginTop: 4
    },


});

/* ---------------- DOCUMENT ---------------- */
const InvoiceDocument = ({ invoice, profile, logoBase64, signUrl, bankDetails, authorityData, base64Sign, customer }) => {
    const safeProfile = profile && typeof profile === "object" ? profile : {};
    const safeBank =
        bankDetails && typeof bankDetails === "object" ? bankDetails : {};
    const companyName = authorityData?.company_name ?? "";
    const safeCustomer =
        customer && typeof customer === "object" ? customer : {};

    const docType = String(invoice?.document_type || "").toLowerCase();
    const isQuotePdf = docType === "quote" || docType === "proforma";
    const isCreditNotePdf = docType === "credit_note";
    const headerTitle = isCreditNotePdf
        ? "CREDIT NOTE"
        : isQuotePdf
            ? "QUOTATION"
            : "TAX INVOICE";
    const recipientLabel = isCreditNotePdf
        ? "ORIGINAL FOR RECIPIENT"
        : isQuotePdf
            ? "ESTIMATE"
            : "ORIGINAL FOR RECIPIENT";

    let items = [];
    try {
        if (Array.isArray(invoice?.items)) {
            items = invoice.items;
        } else if (typeof invoice?.items === "string") {
            items = JSON.parse(invoice.items || "[]");
        }
    } catch {
        items = [];
    }
    if (!Array.isArray(items)) items = [];

    const parseAddress = (raw) => {
        if (raw == null) return null;
        if (typeof raw === "object" && !Array.isArray(raw)) return raw;
        if (typeof raw !== "string") return null;
        try {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === "object" ? parsed : null;
        } catch {
            return null;
        }
    };

    const formatAddress = (addr) => {
        if (!addr) return "-";

        const parts = [
            addr.address,
            addr.city,
            addr.state,
            addr.pincode,
            addr.country,
        ].filter(Boolean);

        return parts.length ? parts.join(", ") : "-";
    };


    const totals = items.reduce(
        (acc, i) => {
            const qty = Number(i.quantity) || 0;
            const rate = Number(i.rate) || 0;
            const taxable = qty * rate;

            const gst = calculateGST(taxable, i.tax);
            acc.taxable += taxable;
            acc.cgst += gst.cgst;
            acc.sgst += gst.sgst;
            return acc;
        },
        { taxable: 0, cgst: 0, sgst: 0 }
    );

    const GST_COLS = {
        hsn: 80,
        taxable: 120,
        rate: 50,
        amount: 70,
        total: 120,
    };

    const grandTotal =
        totals.taxable + totals.cgst + totals.sgst;

    const gstRate =
        items.length > 0 ? Number(items[0].tax) || 0 : 0;

    const cgstRate = gstRate / 2;
    const sgstRate = gstRate / 2;

    const grandTotalWords = numberToIndianWords(grandTotal);

    const billingAddrObj = parseAddress(safeCustomer?.billingAddress);
    const shippingAddrObj = parseAddress(safeCustomer?.shippingAddress);

    const billingAddress = formatAddress(billingAddrObj);
    const shippingAddress = formatAddress(shippingAddrObj);

    const billToDisplayName =
        safeCustomer.companyName ||
        safeCustomer.displayName ||
        safeCustomer.customerName ||
        invoice.customer_name ||
        "";

    const customerGstin =
        safeCustomer.gstNumber ||
        safeCustomer.gst_number ||
        "";

    const customerPhonesLine = [safeCustomer.phone1, safeCustomer.phone2]
        .map((p) => (p != null ? String(p).trim() : ""))
        .filter(Boolean)
        .join(" / ");

    const placeOfSupplyFromBilling = [
        billingAddrObj?.city,
        billingAddrObj?.state,
        billingAddrObj?.pincode,
    ]
        .filter(Boolean)
        .join(", ");

    const placeOfSupplyLine =
        placeOfSupplyFromBilling ||
        [safeProfile.city, safeProfile.state, safeProfile.pincode]
            .filter(Boolean)
            .join(", ");

    const RUPEE = "\u20B9";


    return (
        <Document>
            <Page size="A4" style={styles.page}>

                {/* ================= HEADER ================= */}
                <View style={[styles.row, { marginBottom: 6 }]}>
                    {/* Document title */}
                    <Text style={[styles.bold, { fontSize: 12 }]}>{headerTitle}</Text>

                    {/* Recipient copy label */}
                    <View style={[styles.box, { marginLeft: 10, paddingHorizontal: 6, paddingVertical: 2 }]}>
                        <Text>{recipientLabel}</Text>
                    </View>
                </View>

                {/* ================= COMPANY + INVOICE ================= */}
                <View style={[styles.box, styles.row]}>

                    {/* -------- LEFT : COMPANY -------- */}
                    <View style={[styles.cell, { width: "60%", borderRightWidth: 1 }]}>

                        <View style={styles.row}>
                            {logoBase64 && (
                                <Image
                                    src={logoBase64}
                                    style={{ width: 48, height: 48, marginRight: 8 }}
                                />
                            )}

                            <View style={{ width: "100%" }}>
                                <Text style={[styles.bold, { fontSize: 12 }]}>
                                    {companyName}
                                </Text>

                                {/* Address constrained inside box */}
                                <Text style={[{ fontSize: 10 }]}>
                                    {safeProfile.office_address ?? ""}
                                </Text>

                                <Text style={[{ fontSize: 10 }]}>
                                    GSTIN: {safeProfile.taxNo ?? ""}
                                </Text>
                                <Text style={[{ fontSize: 10 }]}>
                                    Mobile: {safeProfile.mobile ?? ""}
                                </Text>
                                <Text style={[{ fontSize: 10 }]}>
                                    PAN Number: {safeProfile.pan ?? ""}
                                </Text>
                            </View>
                        </View>

                    </View>

                    {/* -------- RIGHT : INVOICE INFO -------- */}
                    <View style={{ width: "40%" }}>

                        {/* Row 1 */}
                        <View style={[styles.row, { borderBottomWidth: 0 }]}>
                            <View style={[styles.cell, { width: "50%", borderRightWidth: 0 }]}>
                                <Text style={styles.bold}>Invoice No.</Text>
                                <Text>{invoice.invoice_number}</Text>
                            </View>

                            <View style={[styles.cell]}>
                                <Text style={styles.bold}>Invoice Date</Text>
                                <Text>{invoice.invoice_date}</Text>
                            </View>
                        </View>

                        {/* Row 2 */}
                        <View style={styles.row}>
                            <View style={[styles.cell, { width: "100%", borderRightWidth: 0 }]}>
                                <Text style={styles.bold}>P.O. Number</Text>
                                <Text>{invoice.po_number || "-"}</Text>
                            </View>
                        </View>

                    </View>
                </View>
                {/* ================= BILL TO / SHIP TO ================= */}
                <View style={[styles.box, styles.row, { borderTopWidth: 0 }]}>

                    {/* LEFT = 60% (same as company block) */}
                    <View
                        style={[
                            styles.cell,
                            {
                                width: "60%",
                                borderRightWidth: 1,   // ✅ aligns with company divider
                                borderColor: "#000",
                            },
                        ]}
                    >
                        <Text style={styles.semibold}>BILL TO</Text>
                        <Text style={[styles.bold, { fontSize: 10 }]}>
                            {billToDisplayName}
                        </Text>
                        {billingAddress && billingAddress !== "-" && (
                            <Text style={[{ fontSize: 10 }]}>
                                Address: {billingAddress}
                            </Text>
                        )}
                        {customerGstin ? (
                            <Text style={[{ fontSize: 10 }]}>
                                GSTIN: {customerGstin}
                            </Text>
                        ) : null}
                        {customerPhonesLine ? (
                            <Text style={[{ fontSize: 10 }]}>
                                Phone: {customerPhonesLine}
                            </Text>
                        ) : null}
                        {placeOfSupplyLine ? (
                            <Text style={[{ fontSize: 10 }]}>
                                Place of Supply: {placeOfSupplyLine}
                            </Text>
                        ) : null}
                    </View>

                    {/* RIGHT = 40% */}
                    <View style={{ width: "40%", padding: 6 }}>
                        <Text style={styles.semibold}>SHIP TO</Text>
                        <Text style={[{ fontSize: 10 }]}>
                            Address: {shippingAddress}
                        </Text>
                        {customerPhonesLine ? (
                            <Text style={[{ fontSize: 10 }]}>
                                Phone: {customerPhonesLine}
                            </Text>
                        ) : null}
                    </View>

                </View>




                {/* ================= ITEMS TABLE (IMAGE 2) ================= */}
                <View style={[styles.itemTable, { borderTopWidth: 0, borderBottomWidth: 0 }]}>
                    {/* ---------- HEADER ---------- */}
                    <View style={[styles.itemRow]}>
                        <Text style={[styles.itemHeaderCell, { width: 30 }]}>NO.</Text>
                        <Text style={[styles.itemHeaderCell, { width: 260 }]}>ITEMS / SERVICES</Text>
                        <Text style={[styles.itemHeaderCell, { width: 80 }]}>HSN/SAC</Text>
                        <Text style={[styles.itemHeaderCell, { width: 60 }]}>QTY.</Text>
                        <Text style={[styles.itemHeaderCell, { width: 60 }]}>RATE</Text>
                        <Text style={[styles.itemLastCell, styles.itemHeaderCell, { width: 80, borderRightWidth: 0 }]}>AMOUNT</Text>
                    </View>



                    {items.map((i, idx) => {
                        const qty = Number(i.quantity) || 0;
                        const rate = Number(i.rate) || 0;
                        const taxable = qty * rate;

                        const gst = calculateGST(taxable, i.tax);

                        return (
                            <View key={idx} style={styles.itemRow}>
                                <Text style={[styles.itemCell, { width: 30, fontSize: 8 }]}>{idx + 1}</Text>

                                <Text style={[styles.itemCell, { width: 260, fontSize: 8 }]}>
                                    {(() => {
                                        const it = i?.item;
                                        const raw =
                                            typeof it === "string"
                                                ? it
                                                : it?.title != null
                                                  ? String(it.title)
                                                  : String(i?.description || "Item");
                                        return raw.replace(/\s*-?\s*Sku-.*/i, "").trim();
                                    })()}
                                </Text>


                                <Text style={[styles.itemCell, { width: 80, fontSize: 8 }]}>{i?.hsn || "-"}</Text>

                                <Text style={[styles.itemCell, { width: 60, textAlign: "center", fontSize: 8 }]}>
                                    {qty}
                                </Text>

                                <Text style={[styles.itemCell, { width: 60, textAlign: "right", fontSize: 8 }]}>
                                    {rate.toFixed(2)}
                                </Text>

                                <Text style={[styles.itemLastCell, { width: 80, textAlign: "right", fontSize: 8 }]}>
                                    {taxable.toFixed(2)}
                                </Text>
                            </View>
                        );
                    })}



                    {/* ---------- BLANK FILLER (keeps CGST at bottom) ---------- */}
                    <View style={[styles.itemRow, { height: 160 }]}>
                        <Text style={[styles.itemCell, { width: 30 }]} />
                        <Text style={[styles.itemCell, { width: 260 }]} />
                        <Text style={[styles.itemCell, { width: 80 }]} />
                        <Text style={[styles.itemCell, { width: 60 }]} />
                        <Text style={[styles.itemCell, { width: 60 }]} />
                        <Text style={[styles.itemLastCell, { width: 80 }]} />
                    </View>

                    {/* ---------- CGST ---------- */}
                    <View style={styles.itemRow}>
                        <Text style={[styles.itemCell, { width: 30 }]} />
                        <Text style={[styles.itemCell, styles.gstRight, { width: 260 }]}>CGST @{cgstRate}%</Text>
                        <Text style={[styles.itemCell, { width: 80 }]}>-</Text>
                        <Text style={[styles.itemCell, { width: 60 }]}>-</Text>
                        <Text style={[styles.itemCell, { width: 60 }]}>-</Text>
                        <Text style={[styles.itemLastCell, styles.gstRight, { width: 80, borderTopWidth: 0 }]}>Rs. {totals.cgst.toFixed(2)}</Text>
                    </View>

                    {/* ---------- SGST ---------- */}
                    <View style={styles.itemRow}>
                        <Text style={[styles.itemCell, { width: 30 }]} />
                        <Text style={[styles.itemCell, styles.gstRight, { width: 260 }]}> SGST @{sgstRate}%</Text>
                        <Text style={[styles.itemCell, { width: 80 }]}>-</Text>
                        <Text style={[styles.itemCell, { width: 60 }]}>-</Text>
                        <Text style={[styles.itemCell, { width: 60 }]}>-</Text>
                        <Text style={[styles.itemLastCell, styles.gstRight, { width: 80, borderTopWidth: 0 }]}>Rs. {totals.sgst.toFixed(2)}</Text>
                    </View>

                    {/* ---------- TOTAL ---------- */}
                    <View style={[styles.itemRow, { borderTopWidth: 1 }]}>
                        <Text style={[styles.itemHeaderCell, { width: 30 }]} />
                        <Text style={[styles.itemHeaderCell, styles.bold, styles.gstRight, { width: 260 }]}>TOTAL</Text>
                        <Text style={[styles.itemHeaderCell, { width: 80 }]} />
                        <Text style={[styles.itemHeaderCell, { width: 60 }]} />
                        <Text style={[styles.itemHeaderCell, { width: 60 }]} />
                        <Text style={[styles.itemHeaderCell, styles.bold, styles.gstRight, { width: 80, borderRightWidth: 0 }]}>Rs. {grandTotal.toFixed(2)}</Text>
                    </View>

                </View>

                {/* ================= GST SUMMARY TABLE ================= */}
                <View style={[styles.gstTable, { borderRightWidth: 0 }]} wrap={false}>

                    {/* HEADER ROW 1 */}
                    <View style={[styles.gstRow]}>
                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.hsn }]}>
                            HSN/SAC
                        </Text>
                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.taxable }]}>
                            Taxable Value
                        </Text>
                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.rate + GST_COLS.amount }]}>
                            CGST
                        </Text>
                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.rate + GST_COLS.amount }]}>
                            SGST
                        </Text>
                        <Text style={[styles.gstCell, styles.gstHeader, { flex: 1 }]}>
                            Total Tax Amount
                        </Text>
                    </View>


                    {/* HEADER ROW 2 */}
                    <View style={[styles.gstRow, { borderBottomWidth: 0 }]}>
                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.hsn }]} />
                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.taxable }]} />

                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.rate }]}>
                            Rate
                        </Text>
                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.amount }]}>
                            Amount
                        </Text>

                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.rate }]}>
                            Rate
                        </Text>
                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.amount }]}>
                            Amount
                        </Text>

                        <Text style={[styles.gstCell, styles.gstHeader, { flex: 1 }]} />

                    </View>


                    {/* DATA ROWS */}
                    {items.map((i, idx) => {
                        const taxable = i.rate * i.quantity;
                        const gst = calculateGST(taxable, i.tax);

                        return (
                            <View key={idx} style={styles.gstRow}>
                                <Text style={[styles.gstCell, { width: GST_COLS.hsn }]}>{i.hsn}</Text>

                                <Text style={[styles.gstCell, styles.gstRight, { width: GST_COLS.taxable }]}>
                                    {taxable.toLocaleString()}
                                </Text>

                                <Text style={[styles.gstCell, { width: GST_COLS.rate }]}>{i.tax / 2}%</Text>
                                <Text style={[styles.gstCell, styles.gstRight, { width: GST_COLS.amount }]}>
                                    {gst.cgst.toFixed(1)}
                                </Text>

                                <Text style={[styles.gstCell, { width: GST_COLS.rate }]}>{i.tax / 2}%</Text>
                                <Text style={[styles.gstCell, styles.gstRight, { width: GST_COLS.amount }]}>
                                    {gst.sgst.toFixed(1)}
                                </Text>

                                <Text style={[styles.gstCell, styles.gstRight, { flex: 1 }]}>
                                    Rs. {(gst.cgst + gst.sgst).toFixed(1)}
                                </Text>


                            </View>
                        );
                    })}

                    {/* TOTAL ROW */}
                    <View style={styles.gstRow}>
                        <Text style={[styles.gstCell, styles.gstBold, { width: GST_COLS.hsn, borderBottomWidth: 0 }]}>
                            Total
                        </Text>

                        <Text style={[styles.gstCell, styles.gstBold, styles.gstRight, { width: GST_COLS.taxable, borderBottomWidth: 0 }]}>
                            {totals.taxable.toLocaleString()}
                        </Text>

                        <Text style={[styles.gstCell, { width: GST_COLS.rate, borderBottomWidth: 0 }]} />
                        <Text style={[styles.gstCell, styles.gstBold, styles.gstRight, { width: GST_COLS.amount, borderBottomWidth: 0 }]}>
                            {totals.cgst.toFixed(1)}
                        </Text>

                        <Text style={[styles.gstCell, { width: GST_COLS.rate, borderBottomWidth: 0 }]} />
                        <Text style={[styles.gstCell, styles.gstBold, styles.gstRight, { width: GST_COLS.amount, borderBottomWidth: 0 }]}>
                            {totals.sgst.toFixed(1)}
                        </Text>

                        <Text style={[styles.gstCell, styles.gstBold, styles.gstRight, { flex: 1, borderBottomWidth: 0 }]}>
                            Rs. {(totals.cgst + totals.sgst).toFixed(1)}
                        </Text>
                    </View>



                </View>
                {/* ================= TOTAL IN WORDS ================= */}
                <View style={[styles.box, { marginTop: 8, padding: 6 }]}>
                    <Text style={styles.bold}>Total Amount (in words)</Text>
                    <Text style={{ marginTop: 4 }}>
                        {grandTotalWords}
                    </Text>
                </View>

                {/* ================= FOOTER ================= */}
                <View style={[styles.box, styles.row, { borderTopWidth: 0 }]}>
                    <View
                        style={{
                            width: "40%",
                            padding: 6,
                            borderRightWidth: 1,
                            alignSelf: "stretch",
                        }}
                    >

                        <Text style={styles.bold}>Notes</Text>
                        <Text style={styles.marginTopitemCell}> {renderNoteAsPDF(invoice.customer_note || '<p>No notes</p>')}</Text>
                    </View>

                    <View
                        style={{
                            width: "35%",
                            padding: 6,
                            borderRightWidth: 1,
                            alignSelf: "stretch",
                        }}
                    >

                        <Text style={styles.bold}>Bank Details</Text>
                        <Text style={styles.marginTopitemCell}>
                            Name: {safeBank.bank_name ?? ""}
                        </Text>
                        <Text style={styles.marginTopitemCell}>
                            IFSC Code: {safeBank.ifsc_code ?? ""}
                        </Text>
                        <Text style={styles.marginTopitemCell}>
                            Account No: {safeBank.account_no ?? ""}
                        </Text>
                        <Text style={styles.marginTopitemCell}>
                            Bank: {safeBank.bank ?? ""}
                        </Text>
                    </View>

                    <View
                        style={{
                            width: "25%",
                            padding: 6,
                            // borderLeftWidth: 1,
                            borderColor: "#000",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {base64Sign && <Image src={base64Sign} style={{ width: 80, height: 40 }} />}
                        <Text style={[styles.center, { marginTop: 4, fontSize: 8 }]}>
                            Authorised Signatory For{"\n"}

                        </Text>
                        <Text style={[styles.center, { marginTop: 4, fontSize: 8 }]}>
                            {companyName}
                        </Text>

                    </View>
                </View>

            </Page>
        </Document >
    );
};